// frontend/src/modules/client/CartPage.jsx
// ═══════════════════════════════════════════════════════════════════════════
// PANIER CLIENT AVEC CHOIX CRÉNEAU DE RETRAIT (VENDREDI / SAMEDI)
// ═══════════════════════════════════════════════════════════════════════════
// Papy 73+ (Marine marchande, Normandie)
// Version avec sélection flexible du jour/horaire de retrait
// ✅ UPGRADE 04/12/2025: alert() → MessageBox (success, error, warning, confirm)
// ✅ FIX 06/12/2025: Calcul date_retrait corrigé (prochain jour correspondant au créneau)
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Minus, Trash2, ShoppingCart, Clock, Calendar, MapPin } from 'lucide-react';
// ✅ NOUVEAU : Import MessageBox
import { useMessageBox } from '../../components/MessageBox';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const CartPage = ({ 
  cart, 
  setCart, 
  user, 
  periodeActive, 
  onUpdateQuantity, 
  onRemoveItem, 
  onConfirmReservation, 
  onBack,
  apiBase 
}) => {
  // ✅ NOUVEAU : Hook MessageBox
  const { showMessage, showConfirm } = useMessageBox();

  // ═══════════════════════════════════════════════════════════════════════════
  // STATE : Créneaux de retrait
  // ═══════════════════════════════════════════════════════════════════════════
  const [creneaux, setCreneaux] = useState([]);
  const [creneauSelectionne, setCreneauSelectionne] = useState(null);
  const [loadingCreneaux, setLoadingCreneaux] = useState(true);

  // ═══════════════════════════════════════════════════════════════════════════
  // CHARGEMENT : Récupérer les créneaux actifs
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    loadCreneaux();
  }, []);

  // Pré-sélectionner le créneau préféré de l'utilisateur
  useEffect(() => {
    if (creneaux.length > 0 && user?.creneau_retrait_prefere) {
      const prefere = creneaux.find(c => c.code_creneau === user.creneau_retrait_prefere);
      if (prefere) {
        setCreneauSelectionne(prefere);
      }
    } else if (creneaux.length > 0 && !creneauSelectionne) {
      // Par défaut, premier créneau
      setCreneauSelectionne(creneaux[0]);
    }
  }, [creneaux, user]);

  const loadCreneaux = async () => {
    try {
      setLoadingCreneaux(true);
      const response = await fetch(`${apiBase || API_BASE}/creneaux-retrait/actifs`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data?.length > 0) {
          setCreneaux(result.data);
        }
      }
    } catch (err) {
      console.error('Erreur chargement créneaux:', err);
    } finally {
      setLoadingCreneaux(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // CALCULS
  // ═══════════════════════════════════════════════════════════════════════════
  const getTotalCart = () => {
    return cart.reduce((total, item) => total + (Number(item.prix_ttc) * item.quantite), 0);
  };

  const getTotalQuantity = () => {
    return cart.reduce((total, item) => total + item.quantite, 0);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ FIX 06/12/2025 : Calcul corrigé de la date réelle de retrait
  // Trouve le PROCHAIN jour correspondant au créneau choisi dans la période active
  // ═══════════════════════════════════════════════════════════════════════════
  const getDateRetraitReelle = () => {
    if (!periodeActive || !creneauSelectionne) return null;
    
    const jourCreneau = creneauSelectionne.jour_semaine; // 5=vendredi, 6=samedi
    
    // Déterminer la plage de la période
    const dateDebut = periodeActive.date_debut 
      ? new Date(periodeActive.date_debut) 
      : new Date();
    const dateFin = periodeActive.date_fin 
      ? new Date(periodeActive.date_fin) 
      : (periodeActive.date_livraison ? new Date(periodeActive.date_livraison) : null);
    
    // Partir d'aujourd'hui
    const aujourd_hui = new Date();
    aujourd_hui.setHours(0, 0, 0, 0);
    
    // Point de départ : le plus grand entre aujourd'hui et début de période
    let dateRecherche = new Date(Math.max(aujourd_hui.getTime(), dateDebut.getTime()));
    dateRecherche.setHours(0, 0, 0, 0);
    
    // Trouver le PROCHAIN jour correspondant au créneau
    let compteur = 0;
    while (dateRecherche.getDay() !== jourCreneau && compteur < 7) {
      dateRecherche.setDate(dateRecherche.getDate() + 1);
      compteur++;
    }
    
    // Si c'est aujourd'hui mais le créneau est passé, prendre la semaine suivante
    const now = new Date();
    if (dateRecherche.toDateString() === now.toDateString()) {
      const heureFinCreneau = parseInt(creneauSelectionne.heure_fin?.substring(0, 2) || '18');
      if (now.getHours() >= heureFinCreneau) {
        dateRecherche.setDate(dateRecherche.getDate() + 7);
      }
    }
    
    // Vérifier qu'on ne dépasse pas la fin de période
    if (dateFin && dateRecherche > dateFin) {
      // Chercher le dernier jour valide dans la période (en reculant)
      let dateRetour = new Date(dateFin);
      dateRetour.setHours(0, 0, 0, 0);
      compteur = 0;
      while (dateRetour.getDay() !== jourCreneau && compteur < 7) {
        dateRetour.setDate(dateRetour.getDate() - 1);
        compteur++;
      }
      // Vérifier que cette date est encore dans le futur
      if (dateRetour >= aujourd_hui) {
        return dateRetour;
      }
      return null; // Aucun créneau valide dans la période
    }
    
    return dateRecherche;
  };

  const formatDateRetrait = () => {
    const date = getDateRetraitReelle();
    if (!date) return 'Date non disponible';
    
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    return date.toLocaleDateString('fr-FR', options);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // CONFIRMATION avec créneau - CORRIGÉE avec MessageBox
  // ═══════════════════════════════════════════════════════════════════════════
  const handleConfirmReservation = () => {
    if (!creneauSelectionne && creneaux.length > 0) {
      // ✅ REMPLACÉ : alert() → warning()
      showMessage('warning', 'Veuillez sélectionner un créneau de retrait');
      return;
    }
    
    const dateRetrait = getDateRetraitReelle();
    if (!dateRetrait) {
      showMessage('error', 'Aucune date de retrait valide pour ce créneau dans la période');
      return;
    }
    
    // Passer le créneau sélectionné au parent
    onConfirmReservation({
      creneau_retrait_id: creneauSelectionne?.id,
      creneau_retrait_code: creneauSelectionne?.code_creneau,
      date_retrait_calculee: dateRetrait.toISOString().split('T')[0]
    });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU : Panier vide
  // ═══════════════════════════════════════════════════════════════════════════
  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <button 
                onClick={onBack}
                className="text-green-600 hover:text-green-800"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-xl font-bold text-green-800">Mon Panier</h1>
            </div>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="text-center">
            <ShoppingCart className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-600 mb-2">Votre panier est vide</h2>
            <p className="text-gray-500 mb-6">Ajoutez des produits depuis le catalogue pour commencer votre réservation.</p>
            <button
              onClick={onBack}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 font-medium"
            >
              Retour au catalogue
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU : Panier avec produits
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack}
              className="text-green-600 hover:text-green-800"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-green-800">Mon Panier</h1>
              <p className="text-sm text-gray-600">{cart.length} article(s) sélectionné(s)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Info période */}
      {periodeActive && (
        <div className="bg-green-100 border-b border-green-200">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <div className="flex items-center gap-2 text-green-800">
              <Calendar className="h-5 w-5" />
              <span className="font-medium">
                Période : {periodeActive.nom || `Semaine du ${new Date(periodeActive.date_debut || periodeActive.date_livraison).toLocaleDateString('fr-FR')}`}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Contenu principal */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ═══════════════════════════════════════════════════════════════════
              COLONNE GAUCHE : Liste des produits
              ═══════════════════════════════════════════════════════════════════ */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b">
                <h2 className="font-semibold text-lg">Articles réservés</h2>
              </div>
              
              <div className="divide-y">
                {cart.map((item) => (
                  <div key={item.id} className="p-4 flex items-center gap-4">
                    {/* Image produit */}
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex-shrink-0 overflow-hidden">
                      {item.image_lien ? (
                        <img 
                          src={item.image_lien} 
                          alt={item.designation}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <ShoppingCart className="h-6 w-6" />
                        </div>
                      )}
                    </div>
                    
                    {/* Infos produit */}
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{item.designation}</h3>
                      <p className="text-sm text-gray-500">
                        {Number(item.prix_ttc).toFixed(2)}€ / {item.unite}
                      </p>
                    </div>
                    
                    {/* Quantité */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onUpdateQuantity(item.id, item.quantite - 1)}
                        className="bg-gray-200 w-8 h-8 rounded hover:bg-gray-300 flex items-center justify-center"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="w-12 text-center font-bold">{item.quantite}</span>
                      <button
                        onClick={() => onUpdateQuantity(item.id, item.quantite + 1)}
                        disabled={item.quantite >= item.quantite_disponible}
                        className="bg-green-500 text-white w-8 h-8 rounded hover:bg-green-600 disabled:bg-gray-400 flex items-center justify-center"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                    
                    {/* Prix total article */}
                    <div className="text-right min-w-[5rem]">
                      <div className="font-bold text-green-600">
                        {(Number(item.prix_ttc) * item.quantite).toFixed(2)}€
                      </div>
                      <button
                        onClick={() => onRemoveItem(item.id)}
                        className="text-red-500 hover:text-red-700 mt-1"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* ═══════════════════════════════════════════════════════════════════
              COLONNE DROITE : Récapitulatif + Choix créneau
              ═══════════════════════════════════════════════════════════════════ */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow sticky top-6">
              <div className="p-6">
                <h2 className="text-lg font-semibold mb-4">Récapitulatif</h2>
                
                {/* Détail du panier */}
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span>Nombre d'articles:</span>
                    <span>{cart.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Quantité totale:</span>
                    <span>{getTotalQuantity()}</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total:</span>
                      <span className="text-green-600">{getTotalCart().toFixed(2)}€</span>
                    </div>
                  </div>
                </div>

                {/* ═══════════════════════════════════════════════════════════════
                    SÉLECTION CRÉNEAU DE RETRAIT
                    ═══════════════════════════════════════════════════════════════ */}
                {creneaux.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Choisir votre créneau de retrait
                    </h3>
                    
                    <div className="space-y-2">
                      {creneaux.map(creneau => {
                        const isSelected = creneauSelectionne?.id === creneau.id;
                        const isPrefere = user?.creneau_retrait_prefere === creneau.code_creneau;
                        
                        return (
                          <label 
                            key={creneau.id}
                            className={`
                              flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all
                              ${isSelected 
                                ? 'bg-blue-600 text-white shadow-md' 
                                : 'bg-white hover:bg-blue-100 border border-gray-200'
                              }
                            `}
                          >
                            <input
                              type="radio"
                              name="creneau_retrait"
                              value={creneau.id}
                              checked={isSelected}
                              onChange={() => setCreneauSelectionne(creneau)}
                              className="sr-only"
                            />
                            
                            <div className={`
                              w-5 h-5 rounded-full border-2 flex items-center justify-center
                              ${isSelected ? 'border-white bg-white' : 'border-gray-400'}
                            `}>
                              {isSelected && (
                                <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                              )}
                            </div>
                            
                            <div className="flex-1">
                              <div className="font-medium flex items-center gap-2">
                                {creneau.libelle}
                                {isPrefere && !isSelected && (
                                  <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded">
                                    ⭐ Préféré
                                  </span>
                                )}
                              </div>
                              <div className={`text-sm flex items-center gap-1 ${isSelected ? 'text-blue-100' : 'text-gray-500'}`}>
                                <Clock className="h-3 w-3" />
                                {creneau.heure_debut?.substring(0,5)} - {creneau.heure_fin?.substring(0,5)}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>

                    {/* Afficher la date calculée */}
                    {creneauSelectionne && (
                      <div className="mt-4 p-3 bg-green-100 rounded-lg">
                        <div className="flex items-center gap-2 text-green-800">
                          <MapPin className="h-4 w-4" />
                          <span className="font-medium">Retrait prévu :</span>
                        </div>
                        <div className="text-green-900 font-bold mt-1 capitalize">
                          {formatDateRetrait()}
                        </div>
                        <div className="text-green-700 text-sm">
                          de {creneauSelectionne.heure_debut?.substring(0,5)} à {creneauSelectionne.heure_fin?.substring(0,5)}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Chargement créneaux */}
                {loadingCreneaux && (
                  <div className="bg-gray-100 rounded-lg p-4 mb-6 text-center text-gray-500">
                    Chargement des créneaux...
                  </div>
                )}
                
                {/* Info client */}
                <div className="bg-gray-50 p-3 rounded-lg mb-6">
                  <h3 className="font-medium text-sm text-gray-700 mb-2">Réservation pour:</h3>
                  <div className="text-sm text-gray-600">
                    <div>{user.nom_prenom}</div>
                    <div>Code: {user.code_client}</div>
                  </div>
                </div>
                
                {/* Avertissement */}
                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg mb-6">
                  <p className="text-xs text-yellow-800">
                    <strong>Réservation sans engagement</strong><br/>
                    Cette demande sera transmise à l'exploitant qui vous confirmera la disponibilité.
                  </p>
                </div>
                
                {/* Boutons d'action */}
                <div className="space-y-3">
                  <button
                    onClick={handleConfirmReservation}
                    disabled={creneaux.length > 0 && !creneauSelectionne}
                    className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {creneaux.length > 0 && !creneauSelectionne 
                      ? 'Sélectionnez un créneau'
                      : 'Confirmer ma réservation'
                    }
                  </button>
                  
                  <button
                    onClick={onBack}
                    className="w-full border border-green-600 text-green-600 py-2 rounded-lg hover:bg-green-50"
                  >
                    Continuer mes achats
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;